import { useContext, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import {
  ActionMenuConfluence,
  ActionMenuSupport,
  ActionMenuThoughtSpot,
  PageCoco,
  PageConfiguration,
  PageContentLibrary,
  PageFileManagement,
  PageIdentityManagement,
} from "../utils/controls";
import { useIntl } from "react-intl";
import IconHomeDefault from "../assets/images/Truman-UI-IdlIcon.svg";
import IconHomeActive from "../assets/images/Truman-UI-IdlIcon-Active.svg";
import IconConfigDefault from "../assets/images/Truman-UI-Nav-Config-Default.svg";
import IconConfigActive from "../assets/images/Truman-UI-Nav-Config-Active.svg";
import IconCocoDefault from "../assets/images/Truman-UI-Nav-Coco-Default.svg";
import IconCocoActive from "../assets/images/Truman-UI-Nav-Coco-Active.svg";
import IconConfluenceDefault from "../assets/images/Truman-UI-Nav-Confluence.svg";
import IconConfluenceDark from "../assets/images/Truman-UI-Nav-Confluence-dark-mode.svg";
import IconThoughtSpot from "../assets/images/Truman-UI-Nav-ThoughtSpot.svg";
import IconSupportDefault from "../assets/images/Truman-UI-Nav-Support.svg";
import IconContentLibraryDefault from "../assets/images/Truman-UI-Nav-ContentLibrary.svg";
import IconContentLibraryActive from "../assets/images/Truman-UI-Nav-ContentLibrary-Active.svg";
import IconFileManangementDefault from "../assets/images/Truman-UI-Nav-File.svg";
import IconFileManagementActive from "../assets/images/Truman-UI-Nav-File-Active.svg";
import { isActionRestricted, isPageRestricted } from "../features/configSlice";
import useProfile from "./useProfile";
import useAuth from "./useAuth";
import { getUrlParams, isRoleCOCO } from "../utils/utils";
import { getMatchedRoleConfig } from "../utils/roles";
import { ThemeContext } from "../components/context/ThemeContext";
import { ROLE_CONFIG } from "../utils/roleConfig";

const useSideBarLayout = () => {
  const { isDarkMode } = useContext(ThemeContext);
  const { tokenData } = useProfile();
  const roles = tokenData?.roles;
  const roleConfig = getMatchedRoleConfig(roles, ROLE_CONFIG);
  const state = useSelector((state) => state.config);
  const intl = useIntl();
  const { scopeStatus } = useSelector((state) => state.controller);
  const { loading: status, controllers } = useSelector(
    (state) => state.controllers,
  );
  const scopesLoaded = ["succeeded", "failed"]?.includes(scopeStatus);
  const controllersLoaded = ["succeeded", "failed"]?.includes(status);
  const isLoaded = ["succeeded", "failed"]?.includes(state?.status);
  const [mainMenu, setMainMenu] = useState([]);
  const [bottomMenu, setBottomMenu] = useState([]);
  const [isExceed, setIsExceed] = useState(false);
  const [loading, setLoading] = useState(false);
  const { isAdmin } = useAuth();

  const isPageRestrictedForUser = (page) => {
    if (!roleConfig.allowedPages.length) return false;
    return !roleConfig.allowedPages.includes(page);
  };
  const cocoParam = () => {
    const queryString = getUrlParams();
    return queryString?.SourceSystem ? location.search : "";
  };

  const getAllMainMenu = () => {
    const menus = [
      !isPageRestricted(state, PageIdentityManagement) &&
      !isPageRestrictedForUser(PageIdentityManagement) && {
        label: intl.formatMessage({
          id: "navbar.id",
          defaultMessage: "ID Management",
        }),
        path: "/ui/identity-management",
        iconDefault: IconHomeDefault,
        iconActive: IconHomeActive,
      },
      !isPageRestricted(state, PageCoco) &&
      !isPageRestrictedForUser(PageCoco) && {
        label: intl.formatMessage({
          id: "navbar.coco",
          defaultMessage: "Coco",
        }),
        path: `/ui/coco/${cocoParam()}`,
        iconDefault: IconCocoDefault,
        iconActive: IconCocoActive,
      },
      !isPageRestricted(state, PageContentLibrary) &&
      !isPageRestrictedForUser(PageContentLibrary) && {
        label: intl.formatMessage({
          id: "navbar.contentlibrary",
          defaultMessage: "Content Library",
        }),
        path: "/ui/contentlibrary",
        iconDefault: IconContentLibraryDefault,
        iconActive: IconContentLibraryActive,
      },
      !isPageRestricted(state, PageFileManagement) &&
      !isPageRestrictedForUser(PageFileManagement) &&
      isAdmin() && {
        label: intl.formatMessage({
          id: "navbar.file-management",
          defaultMessage: "File Management",
        }),
        path: "/ui/file-management",
        iconDefault: IconFileManangementDefault,
        iconActive: IconFileManagementActive,
      },
      !isPageRestricted(state, PageConfiguration) &&
      !isPageRestrictedForUser(PageConfiguration) && {
        label: intl.formatMessage({
          id: "navbar.configuration",
          defaultMessage: "Configuration",
        }),
        path: "/ui/configuration",
        iconDefault: IconConfigDefault,
        iconActive: IconConfigActive,
      },
    ];
    return menus?.filter((item) => item);
  };
  const getBottomMenu = () => {
    const menusBottom = [
      !isActionRestricted(state, ActionMenuThoughtSpot) && {
        label: intl.formatMessage({
          id: "navbar.thought-spot",
          defaultMessage: "ThoughtSpot",
        }),
        path: "https://bayer.thoughtspot.cloud/#/insights/pinboard/0dda4168-1f05-41cf-a9c9-1eab7a39b879/tab/637c0632-2278-46ab-a4fd-a20b59e6614e",
        iconDefault: IconThoughtSpot,
        showOn: "lg",
      },
      !isActionRestricted(state, ActionMenuConfluence) && {
        label: intl.formatMessage({
          id: "navbar.confluence",
          defaultMessage: "Confluence",
        }),
        path: "https://confluence.cloud.bayer.com/display/CM/FAQs",
        iconDefault: isDarkMode ? IconConfluenceDark : IconConfluenceDefault,
      },
      !isActionRestricted(state, ActionMenuSupport) && {
        label: intl.formatMessage({
          id: "navbar.support",
          defaultMessage: "Contact",
        }),
        path: "mailto:",
        iconDefault: IconSupportDefault,
      },
    ];
    return menusBottom;
  };
  const getRequiredSideMenuHeight = () => {
    const mainList = getAllMainMenu()?.filter((item) => item);
    const bottomList = getBottomMenu()?.filter((item) => item);
    const reqMainMenuHeight = mainList?.length * 65;
    const reqBottomHeight = bottomList?.length * 65 + 105;
    return reqMainMenuHeight + reqBottomHeight;
  };
  const checkSpacing = () => {
    const navElement = document.getElementsByClassName("dashboard-nav");
    if (navElement.length) {
      const navHeight = navElement[0].offsetHeight;
      const reqNavHeight = getRequiredSideMenuHeight();
      if (reqNavHeight > navHeight) setIsExceed(true);
      else setIsExceed(false);
    } else setIsExceed(false);
  };

  useEffect(() => {
    if (mainMenu?.length && bottomMenu?.length) checkSpacing();
  }, [mainMenu, bottomMenu]);

  useEffect(() => {
    if (isRoleCOCO(roles)) setLoading(false);
    else {
      if (scopesLoaded && controllersLoaded && isLoaded) setLoading(false);
      else if (controllersLoaded && controllers?.length === 0)
        setLoading(false);
      else setLoading(true);
    }
  }, [scopesLoaded, controllersLoaded, isLoaded, roles]);

  useEffect(() => {
    const mainList = getAllMainMenu();
    const bottomList = getBottomMenu();
    setMainMenu(mainList);
    setBottomMenu(bottomList);
  }, [state, roles, isDarkMode]);
  useEffect(() => {
    window.addEventListener("resize", checkSpacing);
    return () => {
      window.removeEventListener("resize", checkSpacing);
    };
  }, []);

  return { mainMenu, bottomMenu, isExceed, loading, setLoading };
};

export default useSideBarLayout;
